import React, {useState, useCallback, useRef, useEffect, useMemo} from 'react';
import {Box, Typography, Button, Snackbar, Alert} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {Plus} from 'lucide-react';
import {useParams, useNavigate} from 'react-router-dom';
import {useSelector, useDispatch} from 'react-redux';
import {useTheme} from '@mui/material/styles';
import {useAuthFrontendApis} from '@propelauth/frontend-apis-react';
import {Tabs, Tab} from '../../components/Tabs';
import CompanyMembers from './CompanyMembers';
import CompanyDetails from './CompanyDetails';
import InviteUserModal from './InviteUserModal';
import AddUserModal from './AddUserModal';
import {setOrg} from '../../data/session';

const styles = {
  container: {
    padding: '24px',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  header: {
    display: 'grid',
    gridTemplateColumns: '1fr auto 1fr',
    alignItems: 'center',
    marginBottom: '32px',
    minHeight: '48px',
  },
  titleContainer: {
    justifySelf: 'start',
    maxWidth: '400px',
  },
  title: {
    color: '#162523',
    fontSize: '26px',
    fontStyle: 'normal',
    fontWeight: 700,
    lineHeight: '130%' /* 31.2px */,
    letterSpacing: '-0.24px',
  },
  tabsContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gridColumn: 2,
    marginTop: '4px',
  },
  inviteButtonContainer: {
    display: 'flex',
    justifyContent: 'end',
    alignItems: 'center',
    gridColumn: 3,
  },
};

const CompanySettingsPage = () => {
  const {companySlug, tab} = useParams();
  const dispatch = useDispatch();
  const {orgs, parentOrg, hasParentOrg, currentOrg} = useSelector(
    (state) => state.session
  );
  const {fetchOrgSettings} = useAuthFrontendApis();
  const theme = useTheme();
  const navigate = useNavigate();
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [addUserModalOpen, setAddUserModalOpen] = useState(false);
  const [orgSettings, setOrgSettings] = useState(null);
  const companyMembersRef = useRef();

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success', // 'success' or 'error'
  });

  // Get the current tab from the URL, default to 'users'
  const selectedTab = tab || 'users';

  // Find the company by slug (convert orgName to slug format)
  const company = useMemo(() => {
    return orgs.find((org) => {
      const slug = org.orgName.toLowerCase().replace(/\s+/g, '-');
      return slug === companySlug && !org.orgMetadata?.isParentOrganization;
    });
  }, [orgs, companySlug]);

  // Sync currentOrg in Redux when company changes from URL
  // Changing orgs in the sidebar should behave exactly like the org switcher
  useEffect(() => {
    if (!company?.orgId) return;

    // This handles page refreshes where App.jsx might have set an org from localStorage
    if (company.orgId !== currentOrg?.orgId) {
      dispatch(setOrg({org: company}));
      localStorage.setItem('selectedOrgId', company.orgId);
    }
  }, [company, company?.orgId, currentOrg?.orgId, dispatch]);

  // Fetch org settings to check SAML status
  // If hasParentOrg is true, fetch parentOrg settings; otherwise fetch current company settings
  useEffect(() => {
    const orgIdToFetch = hasParentOrg ? parentOrg?.orgId : company?.orgId;
    if (!orgIdToFetch) return;

    const getOrgSettings = async () => {
      try {
        const response = await fetchOrgSettings(orgIdToFetch);

        await response.handle({
          success: (responseData) => {
            setOrgSettings(responseData);
          },
          orgNotFound: () => {
            setOrgSettings(null);
          },
          orgsNotEnabled: () => {
            setOrgSettings(null);
          },
          unauthorized: () => {
            setOrgSettings(null);
          },
          unexpectedOrUnhandled: () => {
            setOrgSettings(null);
          },
        });
      } catch (err) {
        setOrgSettings(null);
      }
    };

    getOrgSettings();
  }, [parentOrg, hasParentOrg, company?.orgId, fetchOrgSettings]);

  // Check if user has admin permissions for this company
  // TODO update this to allow PSO Owner to view even if they are not assigned to the org
  const hasAdminPermissions =
    company &&
    (company.userAssignedRole === 'Admin' ||
      company.userAssignedRole === 'Owner' ||
      company.userAssignedRole === 'InScope Admin');

  const handleTabChange = (event, newValue) => {
    navigate(`/settings/${companySlug}/${newValue}`);
  };

  // Determine which button to show based on hasParentOrg and SAML status
  // - If hasParentOrg is true: always show Add button
  // - If hasParentOrg is false and SAML is disabled: show Invite button
  // - If hasParentOrg is false and SAML is enabled: show no button
  const shouldShowAddButton = hasParentOrg;
  const shouldShowInviteButton =
    !hasParentOrg && orgSettings?.is_saml_enabled === false;

  const handleInviteClick = () => {
    setInviteModalOpen(true);
  };

  const handleAddUserClick = () => {
    setAddUserModalOpen(true);
  };

  const handleShowSnackbar = useCallback(({message, severity}) => {
    setSnackbar({
      open: true,
      message,
      severity,
    });

    // If it's a successful invitation, refresh pending invites
    if (severity === 'success' && message.includes('Invitation sent')) {
      setTimeout(() => {
        companyMembersRef.current?.refreshPendingInvites();
      }, 100);
    }
  }, []);

  if (!company) {
    return (
      <Box sx={styles.container}>
        <Typography variant="h4">Company not found</Typography>
      </Box>
    );
  }

  if (!hasAdminPermissions) {
    return (
      <Box sx={styles.container}>
        <Typography variant="h4" sx={{color: '#D73E3E', marginBottom: '16px'}}>
          Access Denied
        </Typography>
        <Typography variant="body1" sx={{color: '#666', marginBottom: '24px'}}>
          You do not have permission to access settings for this organization.
          Only Admins and Owners can manage company settings.
        </Typography>
        <Button
          onClick={() => navigate('/settings')}
          sx={theme.button.primary.medium}
        >
          Back to Settings
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={styles.container}>
      <Box sx={styles.header}>
        {/* Company Name */}
        <Box sx={styles.titleContainer}>
          <Typography sx={styles.title}>{company.orgName}</Typography>
        </Box>

        {/* Tabs */}
        <Box sx={styles.tabsContainer}>
          <Tabs width="250px" value={selectedTab} onChange={handleTabChange}>
            <Tab
              key="users"
              label="Users"
              value="users"
              sx={{width: '125px'}}
            />
            <Tab
              key="settings"
              label="Settings"
              value="settings"
              sx={{width: '125px'}}
            />
          </Tabs>
        </Box>

        {/* Action Button - Add or Invite */}
        <Box sx={styles.inviteButtonContainer}>
          {selectedTab === 'users' && (
            <>
              {shouldShowAddButton && (
                <Button
                  onClick={handleAddUserClick}
                  sx={{
                    ...theme.button.primary.large,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    width: '120px',
                  }}
                >
                  <Plus size={16} />
                  Add users
                </Button>
              )}
              {shouldShowInviteButton && (
                <Button
                  onClick={handleInviteClick}
                  startIcon={<Plus size={16} />}
                  sx={{
                    ...theme.button.primary.medium,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '3px',
                    width: '140px',
                    alignSelf: 'flex-start',
                    marginTop: '2px',
                  }}
                >
                  Invite user
                </Button>
              )}
            </>
          )}
        </Box>
      </Box>

      {/* Tab Content */}
      <Box sx={{marginTop: '24px'}}>
        {selectedTab === 'users' && company && (
          <CompanyMembers ref={companyMembersRef} company={company} />
        )}
        {selectedTab === 'settings' && company && (
          <CompanyDetails company={company} />
        )}
      </Box>

      {/* Invite User Modal */}
      {company && (
        <InviteUserModal
          open={inviteModalOpen}
          onClose={() => setInviteModalOpen(false)}
          orgId={company.orgId}
          orgName={company.orgName}
          onShowSnackbar={handleShowSnackbar}
        />
      )}

      {/* Add User Modal */}
      {company && hasParentOrg && (
        <AddUserModal
          open={addUserModalOpen}
          onClose={() => setAddUserModalOpen(false)}
          orgId={company.orgId}
          orgName={company.orgName}
          onShowSnackbar={handleShowSnackbar}
          onUserAdded={() => companyMembersRef.current?.refreshMembers()}
        />
      )}

      {/* Snackbar for success and error messages */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((prev) => ({...prev, open: false}))}
        anchorOrigin={{vertical: 'top', horizontal: 'center'}}
        style={{marginTop: '35px'}}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({...prev, open: false}))}
          severity={snackbar.severity}
          icon={
            snackbar.severity === 'success' ? (
              <Box
                sx={{
                  backgroundColor: '#26A993',
                  borderRadius: '50%',
                  width: 22,
                  height: 22,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <CheckIcon sx={{color: 'white', fontSize: 16}} />
              </Box>
            ) : (
              <ErrorOutlineIcon sx={{color: '#D73E3E', fontSize: 22}} />
            )
          }
          sx={{
            width: '100%',
            backgroundColor: '#FFFFFF',
            color: '#000000',
            border: '1px solid #E1E5E4',
            borderRadius: '8px',
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CompanySettingsPage;
